import React, { useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useScopes from '../../utils/useScopes';
import TreeView from '@material-ui/lab/TreeView';
import TreeItem from '@material-ui/lab/TreeItem';
import Box from '@material-ui/core/Box';
import InputLabel from '@material-ui/core/InputLabel';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import Icon from '@afosto/app-components-react/Icon';
import Checkbox from '@afosto/app-components-react/Checkbox';
import merge from 'lodash.merge';

const ScopesTree = ({ label: inputLabel, value, onChange, readOnly }) => {
  const intl = useIntl();
  const [availableScopes, isFetchingScopes] = useScopes();
  const [selected, setSelected] = useState(
    value.reduce((acc, scope) => [...acc, scope?.label ?? scope], []),
  );

  const handleSelect = keys => (ev, checked) => {
    let newSelected = [...selected];
    if (checked) {
      newSelected.push(...keys);
    } else {
      newSelected = newSelected.filter(item => !keys.includes(item));
    }
    setSelected(newSelected);
    if (onChange && typeof onChange === 'function') {
      onChange(newSelected);
    }
  };

  const formattedScopes = (availableScopes ?? []).reduce((acc, scope) => {
    const parts = scope?.value.split(':');
    return merge(acc, {
      [parts[0]]: {
        label: parts[0],
        translationKey: `scopeParts.${parts[0]}`,
        key: parts[0],
        select: { [scope?.value]: scope?.value },
        ...(parts[1]
          ? {
              items: {
                [parts[1]]: {
                  label: parts[1],
                  translationKey: `scopeParts.${parts[1]}`,
                  key: parts.slice(0, 2).join(':'),
                  select: { [scope?.value]: scope?.value },
                  ...(parts[2]
                    ? {
                        items: {
                          [parts[2]]: {
                            label: parts[2],
                            translationKey: `scopeParts.${parts[2]}`,
                            key: parts.slice(0, 3).join(':'),
                            select: { [scope?.value]: scope?.value },
                          },
                        },
                      }
                    : {}),
                },
              },
            }
          : {}),
      },
    });
  }, []);

  const renderItem = settings => {
    const { label, items, key, translationKey, select: selectObject = [] } = settings;
    const select = Object.keys(selectObject);
    const isChecked = select.every(item => selected.includes(item));
    const isIndeterminate = !isChecked && select.some(item => selected.includes(item));

    if (items) {
      return (
        <TreeItem
          key={key}
          nodeId={key}
          label={
            <Box pl={1}>
              <Checkbox
                name={key}
                label={intl.formatMessage({
                  id: translationKey,
                  defaultMessage: label,
                  description: `label of the scope part ${key}`,
                })}
                checked={isChecked}
                indeterminate={isIndeterminate}
                onChange={handleSelect(select)}
                readOnly={readOnly}
                register={false}
              />
            </Box>
          }
        >
          {Object.values(items).map(renderItem)}
        </TreeItem>
      );
    }

    return (
      <TreeItem
        key={key}
        nodeId={key}
        label={
          <Box pl={1}>
            <Checkbox
              name={key}
              label={intl.formatMessage({
                id: translationKey,
                defaultMessage: label,
                description: `label of the scope part ${key}`,
              })}
              checked={isChecked}
              indeterminate={isIndeterminate}
              onChange={handleSelect(select)}
              readOnly={readOnly}
              register={false}
            />
          </Box>
        }
      />
    );
  };

  return (
    <LoadingWrapper isLoading={isFetchingScopes}>
      <Box minHeight="320px" pt={2}>
        <Box pb={2}>
          <InputLabel>{inputLabel}</InputLabel>
        </Box>
        <TreeView
          defaultCollapseIcon={<Icon icon={['fal', 'angle-up']} />}
          defaultExpandIcon={<Icon icon={['fal', 'angle-down']} />}
          disableSelection
        >
          {Object.values(formattedScopes).map(renderItem)}
        </TreeView>
      </Box>
    </LoadingWrapper>
  );
};

ScopesTree.propTypes = {
  label: PropTypes.string,
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
  value: PropTypes.array,
};

ScopesTree.defaultProps = {
  label: '',
  onChange: undefined,
  readOnly: false,
  value: [],
};

export default ScopesTree;
